// src/components/admin/AdminInvitePartnerMemberModal.jsx
import {
  useEffect,
  useState,
} from "react";
import {
  Mail,
  ShieldCheck,
  UserPlus,
  X,
} from "lucide-react";
import { sendPartnerMemberInvitation } from "../../lib/partner/partnerMemberInvitations";

const ROLE_LABELS = {
  owner: "Owner",
  admin: "Administrator",
};

export default function AdminInvitePartnerMemberModal({
  open,
  partner,
  onClose,
  onInvited,
}) {
  const [email, setEmail] =
    useState("");

  const [role, setRole] =
    useState("owner");

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState("");

  useEffect(() => {
    if (!open) {
      setEmail("");
      setRole("owner");
      setSaving(false);
      setError("");

      return undefined;
    }

    setEmail("");
    setError("");

    setRole(
      Number(partner?.owner_count || 0) >
        0
        ? "admin"
        : "owner"
    );

    function handleEscape(event) {
      if (
        event.key === "Escape" &&
        !saving
      ) {
        onClose?.();
      }
    }

    window.addEventListener(
      "keydown",
      handleEscape
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, [
    open,
    partner,
    saving,
    onClose,
  ]);

  if (!open || !partner) {
    return null;
  }

  function handleBackdropClick(event) {
    if (
      event.target ===
        event.currentTarget &&
      !saving
    ) {
      onClose?.();
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();

    try {
      setSaving(true);
      setError("");

      const invitation =
        await sendPartnerMemberInvitation({
          partnerId: partner.id,
          email,
          role,
          expiresInDays: 7,
        });

      await onInvited?.({
        partner,
        invitation,
      });

      onClose?.();
    } catch (submitError) {
      console.error(
        "Sending admin partner invitation failed:",
        submitError
      );

      setError(
        submitError?.message ||
          "The invitation could not be sent."
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      role="presentation"
      onMouseDown={
        handleBackdropClick
      }
      className="fixed inset-0 z-[110] flex items-end justify-center bg-black/45 p-0 sm:items-center sm:p-6"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-invite-partner-member-title"
        className="w-full rounded-t-[28px] bg-white shadow-2xl sm:max-w-lg sm:rounded-[28px]"
      >
        <header className="flex items-start justify-between border-b border-[#EFEFEF] px-6 py-5">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.14em] text-[#FF914D]">
              {partner.name}
            </p>

            <h2
              id="admin-invite-partner-member-title"
              className="mt-1 text-2xl font-black text-[#333333]"
            >
              Invite partner manager
            </h2>

            <p className="mt-2 text-sm leading-relaxed text-[#757575]">
              Send access to an owner or
              administrator for this
              organisation.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Close invitation form"
            className="flex h-10 w-10 items-center justify-center rounded-full text-[#757575] transition hover:bg-[#FFF4EC] hover:text-[#FF914D] disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <form
          onSubmit={handleSubmit}
          className="space-y-5 p-6"
        >
          {error && (
            <div
              role="alert"
              className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
            >
              {error}
            </div>
          )}

          <div className="rounded-2xl border border-[#FFE1CF] bg-[#FFF7ED] p-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="mt-0.5 h-5 w-5 flex-none text-[#FF914D]" />

              <p className="text-sm leading-relaxed text-[#75523B]">
                The recipient will receive
                an email containing the
                secure partner invitation
                link. The invitation expires
                after seven days.
              </p>
            </div>
          </div>

          <div>
            <label
              htmlFor="admin-partner-invite-email"
              className="block text-sm font-black text-[#333333]"
            >
              Email address
            </label>

            <div className="relative mt-2">
              <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#999999]" />

              <input
                id="admin-partner-invite-email"
                type="email"
                value={email}
                onChange={(event) =>
                  setEmail(
                    event.target.value
                  )
                }
                disabled={saving}
                required
                autoComplete="email"
                placeholder="owner@example.com"
                className="w-full rounded-2xl border border-[#DDDDDD] py-3 pl-11 pr-4 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="admin-partner-invite-role"
              className="block text-sm font-black text-[#333333]"
            >
              Partner role
            </label>

            <select
              id="admin-partner-invite-role"
              value={role}
              onChange={(event) =>
                setRole(
                  event.target.value
                )
              }
              disabled={saving}
              className="mt-2 w-full rounded-2xl border border-[#DDDDDD] bg-white px-4 py-3 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
            >
              {Object.entries(
                ROLE_LABELS
              ).map(
                ([
                  value,
                  label,
                ]) => (
                  <option
                    key={value}
                    value={value}
                  >
                    {label}
                  </option>
                )
              )}
            </select>

            <p className="mt-2 text-xs leading-relaxed text-[#757575]">
              Owners have the highest
              partner-level access.
              Administrators can manage the
              organisation but cannot
              replace or restrict an owner.
            </p>
          </div>

          <div className="flex flex-col-reverse gap-3 border-t border-[#EFEFEF] pt-5 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-full border border-[#DDDDDD] px-5 py-3 text-sm font-black text-[#555555] transition hover:bg-[#F7F7F7] disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={
                saving ||
                !email.trim()
              }
              className="inline-flex items-center justify-center rounded-full bg-[#FF914D] px-6 py-3 text-sm font-black text-white transition hover:bg-[#E9782F] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <UserPlus className="mr-2 h-4 w-4" />

              {saving
                ? "Sending invitation…"
                : "Send invitation"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}